import { HashRouter, Navigate, Route, Routes, Outlet, useParams } from 'react-router';
import { lazy, Suspense, useContext } from 'react';
import { PrintableProvider } from '@matrix-forge/ui/contexts/PrintableContext';
import { ForwardDocumentProviderConnected } from '@/providers/ForwardDocumentProviderConnected';
import { RequireAdmin } from '@/components/RequireAdmin';
import { RequireTokenAdmin } from '@/components/RequireTokenAdmin';
import { RequirePaidPlan } from '@/components/RequirePaidPlan';
import { RequireRbvcFeature } from '@/rbvc/RequireRbvcFeature';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { useFeatureFlags } from '@matrix-forge/ui/contexts/FeatureFlagsContext';

// Lazy load all route components for code-splitting
const RootRoute = lazy(() => import('@/routes/RootRoute').then(m => ({ default: m.RootRoute })));
const SnippetsManagerRoute = lazy(() =>
  import('@/routes/SnippetsManagerRoute').then(m => ({ default: m.SnippetsManagerRoute }))
);
const PersonaRoutes = {
  Root: lazy(() => import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Root }))),
  ConversationNewOrChat: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.ConversationNewOrChat }))
  ),
  ConversationFallback: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.ConversationFallback }))
  ),
  History: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.History }))
  ),
  Documents: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Documents }))
  ),
  Jobs: lazy(() => import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Jobs }))),
  Workflows: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Workflows }))
  ),
  Configuration: lazy(() =>
    import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Configuration }))
  ),
  Auto: lazy(() => import('@/routes/PersonaRoute').then(m => ({ default: m.PersonaRoutes.Auto }))),
};
const DocumentRoute = lazy(() =>
  import('@/routes/DocumentRoute').then(m => ({ default: m.DocumentRoute }))
);
const DocumentsRoute = lazy(() =>
  import('@/routes/DocumentsRoute').then(m => ({ default: m.DocumentsRoute }))
);
const AdminRoutes = {
  KnowledgeBase: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.KnowledgeBase }))
  ),
  KnowledgeBaseDetail: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.KnowledgeBaseDetail }))
  ),
  PersonasRoot: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.PersonasRoot }))
  ),
  PersonasIndex: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.PersonasIndex }))
  ),
  PersonasGroups: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.PersonasGroups }))
  ),
  PersonasAuto: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.PersonasAuto }))
  ),
  Secrets: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Secrets }))
  ),
  McpServers: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.McpServers }))
  ),
  Webhooks: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Webhooks }))
  ),
  WorkflowBuilder: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.WorkflowBuilder }))
  ),
  WorkflowEvents: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.WorkflowEvents }))
  ),
  GatewayConfig: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.GatewayConfig }))
  ),
  Organizations: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Organizations }))
  ),
  ConstructFleet: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.ConstructFleet }))
  ),
  SharedLinks: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.SharedLinks }))
  ),
  WorkflowOverview: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.WorkflowOverview }))
  ),
  Triggers: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Triggers }))
  ),
  TeamMembers: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.TeamMembers }))
  ),
  Analytics: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Analytics }))
  ),
  Sync: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Sync }))
  ),
  SyncConfig: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.SyncConfig }))
  ),
  AlertSources: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.AlertSources }))
  ),
  Usage: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Usage }))
  ),
  TimeSeries: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.TimeSeries }))
  ),
  TokenConsole: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.TokenConsole }))
  ),
  Tokens: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.Tokens }))
  ),
  RoleViews: lazy(() =>
    import('@/routes/AdminRoute').then(m => ({ default: m.AdminRoutes.RoleViews }))
  ),
};
const TokenUsageDashboard = lazy(() =>
  import('@/routes/TokenUsageDashboard/TokenUsageDashboard').then(m => ({ default: m.TokenUsageDashboard }))
);
const FallbackRoute = lazy(() =>
  import('@/routes/FallbackRoute').then(m => ({ default: m.FallbackRoute }))
);
const PrintRoute = lazy(() => import('@/routes/PrintRoute').then(m => ({ default: m.PrintRoute })));
const JobInstancesRoute = lazy(() =>
  import('@/routes/JobInstancesRoute').then(m => ({ default: m.JobInstancesRoute }))
);
const JobInstanceRoute = lazy(() =>
  import('@/routes/JobInstanceRoute').then(m => ({ default: m.JobInstanceRoute }))
);
const WorkflowsRoute = lazy(() =>
  import('@/routes/WorkflowsRoute').then(m => ({ default: m.WorkflowsRoute }))
);
const AppsRoute = lazy(() => import('@/routes/AppsRoute').then(m => ({ default: m.AppsRoute })));
const AppViewerRoute = lazy(() =>
  import('@/routes/AppViewerRoute').then(m => ({ default: m.AppViewerRoute }))
);
const NewWorkflowRoute = lazy(() =>
  import('@/routes/WorkflowsRoute').then(m => ({ default: m.NewWorkflowRoute }))
);
const WorkflowRoute = lazy(() =>
  import('@/routes/WorkflowRoute').then(m => ({ default: m.WorkflowRoute }))
);
const ExecuteWorkflowRoute = lazy(() =>
  import('@/routes/ExecuteWorkflowRoute').then(m => ({ default: m.ExecuteWorkflowRoute }))
);
const ExecutionRoute = lazy(() =>
  import('@/routes/ExecutionRoute').then(m => ({ default: m.ExecutionRoute }))
);
const DebuggerRoute = lazy(() =>
  import('@/routes/DebuggerRoute').then(m => ({ default: m.DebuggerRoute }))
);
const PinnedChartsRoute = lazy(() =>
  import('@/routes/PinnedChartsRoute').then(m => ({ default: m.PinnedChartsRoute }))
);
const StrikeKitRoute = lazy(() =>
  import('@/routes/StrikeKitRoute').then(m => ({ default: m.StrikeKitRoute }))
);
const OverviewRoute = lazy(() =>
  import('@/routes/OverviewRoute').then(m => ({ default: m.OverviewRoute }))
);
const DashboardsRoute = lazy(() =>
  import('@/routes/DashboardsRoute').then(m => ({ default: m.DashboardsRoute }))
);
const MeroRoute = lazy(() =>
  import('@/routes/MeroRoute').then(m => ({ default: m.MeroRoute }))
);
const CasesRoute = lazy(() =>
  import('@/routes/cases_route').then(m => ({ default: m.CasesRoute }))
);
const SearchRoute = lazy(() =>
  import('@/routes/search_route').then(m => ({ default: m.SearchRoute }))
);
const AdminCasesRoute = lazy(() =>
  import('@/routes/AdminCasesRoute').then(m => ({ default: m.AdminCasesRoute }))
);
const DataRoutingRoute = lazy(() =>
  import('@/components/data_routing').then(m => ({ default: m.DataRoutingRoute }))
);
const TenantWindowsRoute = lazy(() =>
  import('@/components/TenantWindows/TenantWindowsRoute').then(m => ({
    default: m.TenantWindowsRoute,
  }))
);
const WorkspaceShell = lazy(() =>
  import('@/components/TenantWindows/WorkspaceShell').then(m => ({
    default: m.WorkspaceShell,
  }))
);

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-gray-500">Loading...</div>
  </div>
);

// Wrapper to force ExecuteWorkflowRoute to remount when workflowId changes
const ExecuteWorkflowRouteWithKey = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  return <ExecuteWorkflowRoute key={workflowId} />;
};

export const AppRouter = () => {
  const { userInfo } = useContext(UserInfoContext);
  const features = useFeatureFlags();
  const isPlg = !!userInfo?.isPlg;
  const dataRoutingEnabled = features.dataRoutingEnabled ?? false;

  return (
    /* PrintableProvider intialPathname must match the path base for the print route */
    <PrintableProvider initialPathname="print-preview/document">
      <HashRouter>
        <ForwardDocumentProviderConnected>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Full-screen window-manager desktop (Phase A). A SIBLING of the
                  RootRoute chrome so it replaces the sidebar/shell entirely: the
                  operator's own studio and every tenant are floating iframe
                  windows over the desktop. Self-gates on tenantWindowsEnabled +
                  non-PLG (renders null → redirect handled inside). */}
              <Route
                path="workspace"
                element={
                  <RequireAdmin>
                    <div className="h-screen w-screen overflow-hidden">
                      <WorkspaceShell />
                    </div>
                  </RequireAdmin>
                }
              />
              <Route path="*" element={<RootRoute />}>
                <Route path="persona" element={<Outlet />}>
                  <Route path=":personaId" element={<PersonaRoutes.Root />}>
                    <Route path="conversation" element={<Outlet />}>
                      <Route path="_new" element={<PersonaRoutes.ConversationNewOrChat />} />
                      <Route
                        path=":conversationId"
                        element={<PersonaRoutes.ConversationNewOrChat />}
                      />
                      <Route path="*" element={<PersonaRoutes.ConversationFallback />} />
                    </Route>
                    <Route path="history" element={<PersonaRoutes.History />} />
                    <Route path="documents" element={<PersonaRoutes.Documents />} />
                    <Route path="jobs" element={<PersonaRoutes.Jobs />} />
                    <Route path="workflows" element={<PersonaRoutes.Workflows />} />
                    <Route path="config" element={<PersonaRoutes.Configuration />} />
                    <Route path="*" element={<PersonaRoutes.Auto fallback="conversation" />} />
                  </Route>
                </Route>
                <Route
                  path="documents"
                  element={
                    <RequirePaidPlan>
                      <RequireRbvcFeature feature="documents">
                        <Outlet />
                      </RequireRbvcFeature>
                    </RequirePaidPlan>
                  }
                >
                  <Route path=":conversationId/:documentId" element={<DocumentRoute />} />
                  <Route path="*" element={<DocumentsRoute />} />
                </Route>
                <Route
                  path="jobs"
                  element={
                    <RequirePaidPlan>
                      <RequireRbvcFeature feature="jobs">
                        <JobInstancesRoute />
                      </RequireRbvcFeature>
                    </RequirePaidPlan>
                  }
                />
                <Route
                  path="job/:jobId"
                  element={
                    <RequirePaidPlan>
                      <RequireRbvcFeature feature="jobs">
                        <JobInstanceRoute />
                      </RequireRbvcFeature>
                    </RequirePaidPlan>
                  }
                />
                <Route path="workflows" element={<Outlet />}>
                  <Route index element={<WorkflowsRoute />} />
                  <Route path="new" element={<NewWorkflowRoute />} />
                  <Route path=":workflowId" element={<WorkflowRoute />} />
                  <Route path=":workflowId/execute" element={<ExecuteWorkflowRouteWithKey />} />
                </Route>
                <Route path="snippets" element={<SnippetsManagerRoute />} />
                <Route path="executions/:executionId" element={<ExecutionRoute />} />
                <Route path="executions/:executionId/debug" element={<DebuggerRoute />} />
                <Route path="pinned-charts" element={<PinnedChartsRoute />} />
                <Route path="strikekit" element={<StrikeKitRoute />} />
                <Route path="overview" element={<OverviewRoute />} />
                <Route path="dashboards/*" element={<DashboardsRoute />} />
                <Route path="mero" element={<MeroRoute />} />
                <Route path="apps" element={<AppsRoute />} />
                <Route path="apps/:appAddress" element={<AppViewerRoute />} />
                <Route
                  path="cases/*"
                  element={
                    <RequirePaidPlan>
                      <RequireRbvcFeature feature="case_management">
                        <CasesRoute />
                      </RequireRbvcFeature>
                    </RequirePaidPlan>
                  }
                />
                <Route
                  path="search/*"
                  element={
                    <RequirePaidPlan>
                      <RequireRbvcFeature feature="search">
                        <SearchRoute />
                      </RequireRbvcFeature>
                    </RequirePaidPlan>
                  }
                />
                {dataRoutingEnabled && (
                  <Route
                    path="data-routing/*"
                    element={
                      <RequirePaidPlan>
                        <DataRoutingRoute />
                      </RequirePaidPlan>
                    }
                  />
                )}
                {/* Team settings: hidden for PLG (no team), gated by RBAC for enterprise */}
                {!isPlg && (
                  <Route path="settings" element={<Outlet />}>
                    {userInfo?.features?.rbacEnabled && (
                      <Route
                        path="team"
                        element={
                          <RequireAdmin>
                            <AdminRoutes.TeamMembers />
                          </RequireAdmin>
                        }
                      />
                    )}
                  </Route>
                )}
                <Route path="my-usage" element={<TokenUsageDashboard />} />
                <Route
                  path="tenant-windows"
                  element={
                    <RequireAdmin>
                      <TenantWindowsRoute />
                    </RequireAdmin>
                  }
                />
                <Route path="admin" element={<Outlet />}>
                  <Route
                    path="cases/*"
                    element={
                      <RequirePaidPlan>
                        <AdminCasesRoute />
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="knowledge-bases"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.KnowledgeBase />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="knowledge-bases/knowledge-base/:id"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.KnowledgeBaseDetail />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="personas"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.PersonasRoot />
                      </RequireAdmin>
                    }
                  >
                    <Route path="groups" element={<AdminRoutes.PersonasGroups />} />
                    <Route path="index" element={<AdminRoutes.PersonasIndex />} />
                    <Route path="*" element={<AdminRoutes.PersonasAuto fallback="index" />} />
                  </Route>
                  <Route
                    path="role-views"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.RoleViews />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="secrets"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.Secrets />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="mcp-servers"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.McpServers />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="webhooks"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.Webhooks />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="triggers"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.Triggers />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="workflow-subscriptions"
                    element={<Navigate to="/admin/triggers" replace />}
                  />
                  <Route
                    path="workflow_builder"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.WorkflowBuilder />
                      </RequireAdmin>
                    }
                  />
                  <Route path="workflow-overview" element={<AdminRoutes.WorkflowOverview />} />
                  <Route
                    path="gateways"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.GatewayConfig />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="organizations/*"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.Organizations />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="construct-fleet"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.ConstructFleet />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="workflow-events"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.WorkflowEvents />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="analytics"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.Analytics />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="sync"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.Sync />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="sync/config"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.SyncConfig />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="alert-sources"
                    element={
                      <RequirePaidPlan>
                        <RequireAdmin>
                          <AdminRoutes.AlertSources />
                        </RequireAdmin>
                      </RequirePaidPlan>
                    }
                  />
                  <Route
                    path="ingestion-points"
                    element={<Navigate to="/admin/alert-sources" replace />}
                  />
                  <Route
                    path="usage"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.Usage />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="time-series"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.TimeSeries />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="token-admin"
                    element={
                      <RequireTokenAdmin>
                        <AdminRoutes.TokenConsole />
                      </RequireTokenAdmin>
                    }
                  />
                  <Route
                    path="tokens"
                    element={
                      <RequireTokenAdmin>
                        <AdminRoutes.Tokens />
                      </RequireTokenAdmin>
                    }
                  />
                  <Route
                    path="shared-links"
                    element={
                      <RequireAdmin>
                        <AdminRoutes.SharedLinks />
                      </RequireAdmin>
                    }
                  />
                  <Route path="*" element={<Navigate to="/admin/personas" replace />} />
                </Route>
                <Route
                  path="print-preview/document/:conversationId/:documentId"
                  element={<PrintRoute />}
                />
                <Route path="*" element={<FallbackRoute />} />
              </Route>
            </Routes>
          </Suspense>
        </ForwardDocumentProviderConnected>
      </HashRouter>
    </PrintableProvider>
  );
};
